import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { FitRing } from '../components/FitRing';
import { ContactIcons } from '../components/ContactIcons';
import type { TargetRow } from '../outreachTypes';
import { compactCount, shortDate } from '../outreachFormat';
import { findMessages as messages } from './findMessages';
import { TargetCover } from './TargetCover';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Find.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TargetRowViewProps {
  row: TargetRow;
  onOpen: (targetUuid: string) => void;
}

/**
 * One target (design outreach c1): the fit reasons, the flags and the contact icons. A row in the
 * week limit or with a bad flag is dimmed (spec §8.5). A row with no contact yet is dimmed too and
 * says "No contact found yet"; the drawer still opens.
 */
const TargetRowViewComponent: React.FC<TargetRowViewProps> = ({ row, onOpen }) => {
  const intl = useIntl();
  const isBlocked = row.fatigue !== null || row.flags.includes('looksBought');
  // No contact point at all: nothing to pitch and nothing to keep on a list (owner goal 2026-10-20).
  const noContact = row.contact === null;
  const meta = useMemo(
    () =>
      [
        row.country,
        row.inWorkspace ? intl.formatMessage(messages.inWorkspace) : null,
        noContact ? intl.formatMessage(messages.rowNoContact) : null,
      ]
        .filter((part) => part !== null)
        .join(' · '),
    [intl, noContact, row.country, row.inWorkspace]
  );
  const chips = useMemo(
    () => [
      ...row.fit.reasons.map((reason) => (
        <span
          key={`r-${reason.kind}`}
          className={clsx(shared.chip, reason.kind === 'similarArtists' && shared.chipVio)}
        >
          {reason.label}
        </span>
      )),
      ...row.flags.map((flag) => (
        <span
          key={`f-${flag}`}
          className={clsx(shared.chip, flag === 'looksBought' ? shared.chipNeg : shared.chipMuted)}
        >
          {intl.formatMessage(
            flag === 'looksBought'
              ? messages.flagLooksBought
              : flag === 'stale'
                ? messages.flagStale
                : messages.flagEditorial
          )}
        </span>
      )),
      ...(row.fatigue === null
        ? []
        : [
            <span key="fatigue" className={clsx(shared.chip, shared.chipWarn)}>
              {intl.formatMessage(messages.flagFatigue, { date: shortDate(intl, row.fatigue.freeAgainAt) })}
            </span>,
          ]),
    ],
    [intl, row]
  );

  return (
    <div
      className={clsx(shared.tableRow, styles.targetRow, (isBlocked || noContact) && shared.rowDim)}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row.targetUuid)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onOpen(row.targetUuid);
        }
      }}
      data-testid="target-row"
    >
      <TargetCover className={shared.cover} imageUrl={row.imageUrl} kind={row.kind} />
      <span className={shared.cellTitle}>
        <b>{row.name}</b>
        <small>{meta}</small>
      </span>
      <span className={clsx(shared.cellNum, styles.hideNarrow)}>{compactCount(intl, row.followers)}</span>
      <span className={clsx(styles.why, styles.hideNarrow)}>{chips}</span>
      <span className={styles.hideNarrow}>
        <ContactIcons contact={row.contact} />
      </span>
      <span className="d-flex justify-content-end">
        <FitRing score={row.fit.score} />
      </span>
    </div>
  );
};

export const TargetRowView = TypedMemo(TargetRowViewComponent);
